/**
 * Area Page Content - Allys Theme
 * 
 * Client-side component that renders the area page content
 * with luxury styling matching the sample UI design.
 */

'use client';

import {
  Anchor,
  ArrowDown,
  ArrowsOut,
  ArrowUpRight,
  ChartLineUp,
  CircleNotch,
  Compass,
  Drop,
  ForkKnife,
  Flag,
  Globe,
  GraduationCap,
  MapPin,
  NavigationArrow,
  ShoppingBag,
  Star,
  Stethoscope,
  Sun,
  Tree,
  TrendUp,
  Waves,
  Wind,
  X
} from '@phosphor-icons/react';
import { fetchAreaWeather, getCardinalDirection, type AreaWeatherData } from '@routeroyal/theme-core';
import Image from 'next/image';
import { useCallback, useEffect, useState } from 'react';

interface AreaData {
  slug: string;
  name: string;
  region: string;
  description: string;
  extendedDescription?: string;
  heroImage: string;
  heroVideo?: string;
  highlights?: string[];
  coordinates?: string;
  latitude?: number;
  longitude?: number;
  priceRange?: string;
  marketStats?: {
    totalListings?: number;
    averagePrice?: string;
    priceChange?: string;
    daysOnMarket?: number;
    averageYield?: string;
    growth?: {
      percentage: string;
      period: string;
    };
  };
}

interface AreaRegionGroup {
  region: string;
  areas: { slug: string; name: string }[];
}

interface AmenityItem {
  name: string;
  category: string;
  description: string;
}

interface AttractionData {
  items: AmenityItem[];
  places: Array<{
    title: string;
    uri: string;
  }>;
}

// Default/fallback amenities by area - used when API is unavailable
const DEFAULT_AMENITIES: Record<string, AmenityItem[]> = {
  'grand-baie': [
    { name: 'Northfields International School', category: 'Education', description: 'Providing a globally recognised curriculum and diverse extracurriculars, Northfields International School is essential for expat families seeking premium education for their children in Mauritius.' },
    { name: 'Mont Choisy Le Golf', category: 'Golf', description: 'A championship course designed by Peter Matkovich, Mont Choisy Le Golf offers discerning residents a world-class golfing experience amidst stunning Mauritian landscapes.' },
    { name: 'C-Care Grand Baie', category: 'Wellness', description: 'C-Care Grand Baie offers state-of-the-art medical facilities and personalised care, ensuring that sophisticated residents have immediate access to top-tier private healthcare services.' },
  ],
  'belle-mare': [
    { name: 'Anahita Golf Course', category: 'Golf', description: 'An Ernie Els signature course, Anahita offers 18 holes of championship golf along the stunning east coast lagoon.' },
    { name: 'Four Seasons Resort', category: 'Wellness', description: 'World-class spa and wellness facilities within the prestigious Four Seasons at Anahita.' },
    { name: 'Constance Belle Mare Plage', category: 'Dining', description: 'Fine dining experiences at one of Mauritius most celebrated beachfront resorts.' },
  ],
  'tamarin': [
    { name: 'Tamarina Golf Club', category: 'Golf', description: 'A spectacular 18-hole course set against the dramatic Rempart mountain backdrop on the west coast.' },
    { name: 'Rivière Noire Bay', category: 'Yachting', description: 'Premier sailing and deep-sea fishing destination, home to the annual Marlin World Cup.' },
    { name: 'La Balise Marina', category: 'Yachting', description: 'Exclusive marina facilities for yacht owners and sailing enthusiasts.' },
  ],
  'flic-en-flac': [
    { name: 'Casela Nature Park', category: 'Leisure', description: 'An adventure and nature reserve offering unique wildlife encounters and outdoor activities.' },
    { name: 'Sugar Beach Resort', category: 'Wellness', description: 'Five-star wellness and spa facilities on one of the island longest beaches.' },
    { name: 'Westcoast International School', category: 'Education', description: 'British curriculum education serving the west coast expatriate community.' },
  ],
  'default': [
    { name: 'International School', category: 'Education', description: 'Quality international education options for families with globally recognised curricula.' },
    { name: 'Golf & Country Club', category: 'Golf', description: 'Championship golf courses offering world-class facilities and stunning views.' },
    { name: 'Medical Centre', category: 'Wellness', description: 'Modern healthcare facilities providing comprehensive medical services.' },
  ],
};

// Fetch attractions from API
async function fetchAttractions(areaName: string): Promise<AttractionData | null> {
  try {
    const response = await fetch(`/api/areas/attractions?area=${encodeURIComponent(areaName)}`);
    const result = await response.json();
    
    if (result.success && result.data) {
      return result.data;
    }
    return null;
  } catch (error) {
    console.error('Failed to fetch attractions:', error);
    return null;
  }
}

interface AreaContentAllysProps {
  data: AreaData;
  regions?: AreaRegionGroup[];
  locale?: string;
  messages?: Record<string, string>;
  propertyBasePath?: string;
  areaBasePath?: string;
}

export function AreaContentAllys({
  data,
  messages,
}: AreaContentAllysProps) {
  const [isMapFullScreen, setIsMapFullScreen] = useState(false);
  const [weather, setWeather] = useState<AreaWeatherData | null>(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [attractions, setAttractions] = useState<AttractionData | null>(null);
  const [attractionsLoading, setAttractionsLoading] = useState(true);

  // Fetch weather data
  useEffect(() => {
    if (!data.latitude || !data.longitude) return;
    
    const loadWeather = async () => {
      setWeatherLoading(true);
      try {
        const weatherData = await fetchAreaWeather(data.latitude!, data.longitude!);
        if (weatherData) {
          setWeather(weatherData);
        }
      } catch (error) {
        console.error('Failed to fetch weather:', error);
      } finally {
        setWeatherLoading(false);
      }
    };

    loadWeather();
  }, [data.latitude, data.longitude]);

  // Fetch attractions/amenities data
  useEffect(() => {
    const loadAttractions = async () => {
      setAttractionsLoading(true);
      try {
        const attractionsData = await fetchAttractions(data.name);
        if (attractionsData && attractionsData.items.length > 0) {
          setAttractions(attractionsData);
        }
      } catch (error) {
        console.error('Failed to fetch attractions:', error);
      } finally {
        setAttractionsLoading(false);
      }
    };

    loadAttractions();
  }, [data.name]);

  const handleCloseMap = useCallback(() => {
    setIsMapFullScreen(false);
  }, []);

  // Get amenities for this area (prefer live API data, fallback to static)
  const amenities: AmenityItem[] = attractions?.items.length 
    ? attractions.items 
    : (DEFAULT_AMENITIES[data.slug] ?? DEFAULT_AMENITIES['default'] ?? []);
  
  // Get verified places from Google Maps grounding (if available)
  const verifiedPlaces = attractions?.places || [];

  // Parse coordinates for display
  const coordParts = data.coordinates?.split(',') || [];
  const latDisplay = coordParts[0]?.trim() || `${data.latitude?.toFixed(2)}° S`;
  const lngDisplay = coordParts[1]?.trim() || `${data.longitude?.toFixed(2)}° E`;

  // Map coordinates and zoom level
  const lat = data.latitude || -20.01;
  const lng = data.longitude || 57.58;
  const zoom = 14;

  return (
    <div className="font-sans text-[var(--color-text)] bg-[#f8f9fa] min-h-screen selection:bg-[var(--color-primary)] selection:text-white overflow-x-hidden">
      {/* ========================================= */}
      {/* HERO SECTION */}
      {/* ========================================= */}
      <div className="relative h-[85vh] md:h-screen w-full overflow-hidden">
        {/* Background Media */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 z-10 bg-gradient-to-b from-black/40 via-transparent to-black/30" />
          {data.heroVideo ? (
            <video
              autoPlay
              muted
              loop
              playsInline
              poster={data.heroImage}
              className="absolute inset-0 h-full w-full object-cover animate-subtle-zoom"
            >
              <source src={data.heroVideo} type="video/mp4" />
            </video>
          ) : (
            <Image
              src={data.heroImage}
              alt={`${data.name}, ${data.region} - Mauritius`}
              fill
              priority
              className="object-cover animate-subtle-zoom"
              sizes="100vw"
            />
          )}
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20 text-white/80 flex flex-col items-center gap-2 animate-bounce-gentle">
          <span className="text-[10px] tracking-[0.3em] uppercase drop-shadow-md font-inter">
            Scroll
          </span>
          <ArrowDown size={20} weight="light" className="drop-shadow-md" />
        </div>
      </div>

      {/* ========================================= */}
      {/* MAIN CONTENT CONTAINER */}
      {/* ========================================= */}
      <div className="relative z-30 container mx-auto px-6 md:px-12 -mt-24 md:-mt-48 pb-20">
        
        {/* ----------------------------------------- */}
        {/* FLOATING TITLE CARD */}
        {/* ----------------------------------------- */}
        <div className="bg-white rounded-[2rem] p-8 md:p-12 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.15)] mb-12 flex flex-col lg:flex-row justify-between items-start lg:items-end gap-8 relative overflow-hidden transition-all duration-500 border border-gray-100/80">
          {/* Decorative Background Element */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-[var(--color-primary)]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />

          {/* Left: Region Badge + Area Name */}
          <div className="relative z-10 w-full lg:w-2/3">
            <div className="flex items-center gap-4 mb-4">
              <span className="px-4 py-1.5 rounded-full border border-[var(--color-primary)]/30 text-[var(--color-primary)] text-[10px] font-bold tracking-[0.2em] uppercase bg-[var(--color-primary)]/5 font-inter">
              {data.region}
            </span>
            </div>
            <h1 className="font-serif text-5xl md:text-7xl text-[var(--color-text)] leading-[1.1]">
              {data.name}
            </h1>
          </div>

          {/* Right: Coordinates Card */}
          <div className="relative z-10 w-full lg:w-1/3 flex flex-col items-start lg:items-end">
            <div className="p-6 bg-gray-50/80 rounded-2xl border border-gray-100 w-full lg:w-auto backdrop-blur-sm">
              <div className="flex items-center gap-3 text-[var(--color-text-muted)] mb-2">
                <Globe size={16} weight="light" className="opacity-60" />
                <span className="text-xs font-mono uppercase tracking-widest">
                  {messages?.coordinates || 'Coordinates'}
                </span>
              </div>
              <p className="font-serif text-xl md:text-2xl text-[var(--color-text)] italic">
                {latDisplay}
              </p>
              <p className="font-serif text-xl md:text-2xl text-[var(--color-primary)] italic">
                {lngDisplay}
              </p>
            </div>
          </div>
        </div>

        {/* ----------------------------------------- */}
        {/* OVERVIEW + MAP GRID */}
        {/* ----------------------------------------- */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
          
          {/* Overview Card - 8 cols */}
          <div className="lg:col-span-8">
            <div className="bg-white rounded-[2rem] p-10 md:p-14 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.08)] hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.12)] border border-gray-100/80 transition-all duration-500 h-full">
              <h2 className="font-serif text-3xl md:text-4xl text-[var(--color-text)] mb-8">
                {messages?.theOverview || 'The Overview'}
              </h2>
              
              <div className="prose prose-lg max-w-none">
                {/* First sentence - larger, serif */}
                <p className="text-xl md:text-2xl font-serif text-[var(--color-text)]/80 leading-relaxed mb-6">
                  {data.description.split('.')[0]}.
                </p>
                
                {/* Rest of description */}
                <p className="text-gray-600 font-light leading-loose font-inter">
                  {data.description.split('.').slice(1).join('.').trim()}
                </p>
                
                {/* Extended area content */}
                {data.extendedDescription && (
                  <p className="text-gray-600 font-light leading-loose mt-6 font-inter">
                    {data.extendedDescription}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Map Card - 4 cols */}
          <div className="lg:col-span-4">
            <div className="bg-white p-2 rounded-[2rem] shadow-[0_4px_20px_-4px_rgba(0,0,0,0.08)] hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.12)] border border-gray-100/80 h-full min-h-[500px] hover:-translate-y-1 transition-all duration-500 relative overflow-hidden group">
              <div className="relative w-full h-full min-h-[480px] rounded-[1.8rem] overflow-hidden bg-[#e8e8e8] isolate">
                
                {/* Static Map Background - CARTO Light Tiles */}
                <div className="absolute inset-0 map-tiles-container">
                  {/* Generate a 3x3 grid of tiles around the center point */}
                  {[-1, 0, 1].map(dy => (
                    [-1, 0, 1].map(dx => {
                      const tileX = Math.floor((lng + 180) / 360 * Math.pow(2, zoom)) + dx;
                      const tileY = Math.floor((1 - Math.log(Math.tan(lat * Math.PI / 180) + 1 / Math.cos(lat * Math.PI / 180)) / Math.PI) / 2 * Math.pow(2, zoom)) + dy;
                      return (
                        // eslint-disable-next-line @next/next/no-img-element -- Map tiles from external CDN
                        <img
                          key={`${dx}-${dy}`}
                          src={`https://a.basemaps.cartocdn.com/light_all/${zoom}/${tileX}/${tileY}.png`}
                          alt=""
                          className="absolute w-[256px] h-[256px] object-cover"
                          style={{
                            left: `calc(50% + ${dx * 256}px - 128px)`,
                            top: `calc(50% + ${dy * 256}px - 128px)`,
                          }}
                          loading="lazy"
                        />
                      );
                    })
                  ))}
                </div>

                {/* Custom Gold Marker with Pulse Animation */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                  {/* Pulse ring */}
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-[#d4af37]/20 animate-marker-pulse" />
                  {/* Marker dot */}
                  <div className="relative w-4 h-4 rounded-full bg-[#d4af37] border-[3px] border-white shadow-[0_4px_10px_rgba(0,0,0,0.3)]" />
                </div>

                {/* Area Name Label on Map */}
                <div className="absolute top-1/2 left-1/2 translate-x-2 -translate-y-1 z-10 pointer-events-none">
                  <span className="text-[11px] text-gray-600 font-medium whitespace-nowrap bg-white/80 px-1.5 py-0.5 rounded">
                    {data.name}
                  </span>
                </div>

                {/* Overlay Content - Bottom Card */}
                <div className="absolute bottom-0 left-0 right-0 p-6 z-10 pointer-events-none">
                  <div className="flex justify-between items-end">
                    <div className="pointer-events-auto bg-white/95 backdrop-blur-sm p-4 rounded-2xl shadow-sm border border-gray-100/50">
                      <div className="flex items-center gap-2 mb-1 text-[var(--color-text-muted)]">
                        <MapPin size={14} weight="fill" className="text-[var(--color-primary)]" />
                        <span className="text-xs font-mono tracking-widest uppercase">
                          {messages?.locationMap || 'Location Map'}
                        </span>
                      </div>
                      <h3 className="font-serif text-lg text-[var(--color-text)] leading-none">
                        {data.name}
                      </h3>
                    </div>
                  </div>
                </div>

                {/* Fullscreen Button */}
                <button 
                  onClick={() => setIsMapFullScreen(true)}
                  className="absolute top-4 right-4 z-20 w-10 h-10 bg-white rounded-full flex items-center justify-center text-[var(--color-text)] shadow-lg hover:scale-110 transition-transform cursor-pointer"
                  title="View Fullscreen"
                >
                  <ArrowsOut size={18} weight="bold" />
                </button>
              </div>
          </div>
        </div>
      </div>

        {/* ----------------------------------------- */}
        {/* STATS SECTION - Full Width */}
        {/* ----------------------------------------- */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          
          {/* Market Analysis Card */}
          <div className="bg-white p-6 md:p-8 rounded-[2rem] shadow-[0_4px_20px_-4px_rgba(0,0,0,0.08)] hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.12)] border border-gray-100/80 flex flex-col h-full hover:-translate-y-1 transition-all duration-500">
            <div className="flex justify-between items-start mb-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-2 h-2 rounded-full bg-[var(--color-primary)]"></span>
                  <p className="text-[10px] text-[var(--color-text-muted)] font-mono tracking-[0.2em] uppercase">
                    {messages?.marketIntelligence || 'Market Intelligence'}
                  </p>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl text-[var(--color-text)]">
                  {messages?.propertyTrends || 'Property Trends'}
                </h3>
              </div>
              
              {/* Time Period Selector */}
              <div className="hidden md:flex gap-1 bg-gray-50 p-1 rounded-full border border-gray-100 h-fit">
                {['1Y', '3Y', '5Y'].map((period) => (
                  <button 
                    key={period}
                    className={`px-3 py-1 rounded-full text-[10px] font-bold tracking-wider transition-all duration-300 ${
                      period === '5Y' 
                        ? 'bg-[var(--color-text)] text-white shadow-md' 
                        : 'text-gray-400 hover:text-[var(--color-text)]'
                    }`}
                  >
                    {period}
                  </button>
                ))}
              </div>
            </div>

            {/* Price Display */}
            <div className="flex-1 flex flex-col justify-center items-center py-8">
              <div className="text-center">
                <p className="text-sm text-[var(--color-text-muted)] mb-2 font-inter">
                  Average Property Price
                </p>
                <p className="font-serif text-4xl md:text-5xl text-[var(--color-text)]">
                  {data.priceRange || data.marketStats?.averagePrice || '$1.2M - $3.5M'}
                </p>
        </div>

              {/* Trend indicator */}
              <div className="mt-6 flex items-center gap-2">
                <ChartLineUp size={24} weight="duotone" className="text-[var(--color-primary)]" />
                <span className="text-sm text-[var(--color-text-muted)] font-inter">
                  Strong growth in luxury segment
              </span>
              </div>
            </div>

            {/* Bottom Stats */}
            <div className="grid grid-cols-2 gap-8 pt-6 border-t border-gray-100 mt-auto">
              <div>
                <span className="text-gray-400 text-[10px] uppercase tracking-widest font-mono block mb-1">
                  {messages?.avgYield || 'Avg. Yield'}
                </span>
                <span className="text-[var(--color-text)] font-serif text-2xl">
                  {data.marketStats?.averageYield || '4.2%'}
                </span>
              </div>
              <div>
                <span className="text-gray-400 text-[10px] uppercase tracking-widest font-mono block mb-1">
                  {messages?.growth || 'Growth'} ({data.marketStats?.growth?.period || '5Y'})
                </span>
                <div className="flex items-center gap-3">
                  <span className="text-[var(--color-text)] font-serif text-2xl">
                    {data.marketStats?.growth?.percentage || '+45%'}
                  </span>
                  <span className="bg-green-50 text-green-700 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 border border-green-100">
                    <TrendUp size={10} weight="bold" />
                    High
              </span>
                </div>
              </div>
            </div>
          </div>

          {/* Lifestyle & Highlights Card */}
          <div className="bg-white p-6 md:p-8 rounded-[2rem] shadow-[0_4px_20px_-4px_rgba(0,0,0,0.08)] hover:shadow-[0_20px_40px_-12px_rgba(0,0,0,0.12)] border border-gray-100/80 flex flex-col h-full hover:-translate-y-1 transition-all duration-500">
            <div className="mb-6 flex justify-between items-start">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-2 h-2 rounded-full bg-[var(--color-text)]"></span>
                  <p className="text-[10px] text-[var(--color-text-muted)] font-mono tracking-[0.2em] uppercase">
                    {messages?.lifestyleAudit || 'Lifestyle Audit'}
                  </p>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl text-[var(--color-text)]">
                  {messages?.keyHighlights || 'Key Highlights'}
                </h3>
              </div>
              {weatherLoading && (
                <CircleNotch size={14} weight="bold" className="text-[var(--color-primary)] animate-spin mt-2" />
              )}
            </div>

            {/* Highlights List */}
            <ul className="space-y-4 mb-6 flex-1">
              {(data.highlights || ['Luxury Living', 'World-class Amenities', 'Prime Location']).map((highlight, idx) => (
                <li 
                  key={idx} 
                  className="flex items-start gap-4 text-[var(--color-text)] group"
                >
                  <span className="mt-1 w-6 h-6 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center text-[var(--color-primary)] group-hover:bg-[var(--color-primary)] group-hover:text-white transition-colors duration-300 shrink-0">
                    <ArrowUpRight size={12} weight="bold" />
                  </span>
                  <span className="font-light text-lg leading-relaxed border-b border-transparent group-hover:border-[var(--color-primary)]/20 transition-all font-inter">
                    {highlight}
                  </span>
                </li>
              ))}
            </ul>

            {/* Weather Data */}
            <div className="mt-auto pt-6 border-t border-gray-100 grid grid-cols-3 gap-3">
              {/* Temperature */}
              <div className="text-center p-3 rounded-2xl bg-gray-50/50 border border-gray-100 group hover:border-[var(--color-primary)]/30 transition-colors">
                <Sun size={18} weight="duotone" className="mx-auto mb-2 text-[var(--color-primary)] group-hover:scale-110 transition-transform" />
                <span className="block text-[10px] text-gray-400 uppercase tracking-wider mb-1 font-mono">
                  {messages?.temperature || 'Temp'}
                </span>
                <span className="font-mono text-sm text-[var(--color-text)] font-bold">
                  {weather ? `${weather.temperature}°C` : '--'}
                </span>
              </div>
              
              {/* Wind */}
              <div className="text-center p-3 rounded-2xl bg-gray-50/50 border border-gray-100 group hover:border-[var(--color-primary)]/30 transition-colors">
                <Wind size={18} weight="duotone" className="mx-auto mb-2 text-[var(--color-primary)] group-hover:scale-110 transition-transform" />
                <span className="block text-[10px] text-gray-400 uppercase tracking-wider mb-1 font-mono">
                  {messages?.wind || 'Wind'}
                </span>
                <span className="font-mono text-sm text-[var(--color-text)] font-bold whitespace-nowrap">
                  {weather ? `${getCardinalDirection(weather.windDirection)} ${weather.windSpeed}km` : '--'}
                </span>
              </div>
              
              {/* Humidity */}
              <div className="text-center p-3 rounded-2xl bg-gray-50/50 border border-gray-100 group hover:border-[var(--color-primary)]/30 transition-colors">
                <Drop size={18} weight="duotone" className="mx-auto mb-2 text-[var(--color-primary)] group-hover:scale-110 transition-transform" />
                <span className="block text-[10px] text-gray-400 uppercase tracking-wider mb-1 font-mono">
                  {messages?.humidity || 'Hum'}
                </span>
                <span className="font-mono text-sm text-[var(--color-text)] font-bold">
                  {weather ? `${weather.humidity}%` : '--'}
              </span>
              </div>
            </div>
          </div>
        </div>

        {/* ----------------------------------------- */}
        {/* CURATED GUIDE - LIFESTYLE & AMENITIES */}
        {/* ----------------------------------------- */}
        <div className="bg-white rounded-[2rem] shadow-[0_4px_20px_-4px_rgba(0,0,0,0.08)] border border-gray-100/80 relative overflow-hidden group w-full mb-12">
          {/* Decorative background compass icon */}
          <Compass 
            size={500} 
            weight="thin" 
            className="absolute -bottom-24 -left-24 text-gray-50 rotate-12 transition-transform group-hover:rotate-[15deg] duration-[2000ms] pointer-events-none" 
          />

          <div className="relative z-10 p-8 md:p-12">
            {/* Section Header */}
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-10 rounded-full bg-[var(--color-text)] flex items-center justify-center shadow-lg">
                <Star size={18} weight="fill" className="text-[var(--color-primary)]" />
              </div>
              <div>
                <p className="text-[10px] text-[var(--color-text-muted)] font-mono tracking-[0.2em] uppercase">
                  {messages?.curatedGuide || 'Curated Guide'}
                </p>
                <h3 className="font-serif text-2xl md:text-3xl text-[var(--color-text)]">
                  {messages?.lifestyleAmenities || 'Lifestyle & Amenities'}
                </h3>
              </div>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16">
              {/* Left Column: Amenities List */}
              <div className="lg:col-span-7 order-2 lg:order-1">
                {/* Loading State */}
                {attractionsLoading && (
                  <div className="flex items-center gap-3 mb-6 p-4 bg-[var(--color-primary)]/5 rounded-2xl">
                    <div className="w-3 h-3 rounded-full bg-[var(--color-primary)] animate-pulse"></div>
                    <span className="text-sm text-[var(--color-text-muted)] font-inter">
                      {messages?.loadingAttractions || 'Discovering local amenities...'}
                    </span>
                  </div>
                )}

                {/* Data Source Indicator */}
                {!attractionsLoading && attractions?.items.length && (
                  <div className="flex items-center gap-2 mb-6 text-xs text-gray-400">
                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                    <span className="font-mono uppercase tracking-wider">AI-curated • Live data</span>
          </div>
        )}

                {amenities.map((amenity, index) => {
                  // Get category icon
                  const getCategoryIcon = (category: string) => {
                    const lower = category.toLowerCase();
                    if (lower.includes('school') || lower.includes('education')) return <GraduationCap size={20} weight="regular" />;
                    if (lower.includes('clinic') || lower.includes('medical') || lower.includes('wellness')) return <Stethoscope size={20} weight="regular" />;
                    if (lower.includes('dining') || lower.includes('restaurant')) return <ForkKnife size={20} weight="regular" />;
                    if (lower.includes('marina') || lower.includes('yacht') || lower.includes('boat')) return <Anchor size={20} weight="regular" />;
                    if (lower.includes('shop') || lower.includes('mall')) return <ShoppingBag size={20} weight="regular" />;
                    if (lower.includes('golf')) return <Flag size={20} weight="regular" />;
                    if (lower.includes('beach') || lower.includes('club')) return <Waves size={20} weight="regular" />;
                    return <Tree size={20} weight="regular" />;
                  };

                  return (
                    <div key={index} className="mb-10 last:mb-0 group/item">
                      <div className="flex items-start gap-4 mb-3">
                        <div className="w-10 h-10 rounded-2xl bg-[var(--color-primary)]/5 flex items-center justify-center text-[var(--color-text)] shrink-0 border border-gray-100 group-hover/item:bg-[var(--color-primary)] group-hover/item:text-white transition-colors duration-300">
                          {getCategoryIcon(amenity.category)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-serif text-xl md:text-2xl text-[var(--color-text)] group-hover/item:text-[var(--color-primary)] transition-colors duration-300 leading-tight">
                              {amenity.name}
                            </h4>
                          </div>
                          <span className="text-[10px] font-mono text-[var(--color-primary)] uppercase tracking-widest bg-[var(--color-primary)]/5 px-2 py-0.5 rounded-full inline-block">
                            {amenity.category}
                          </span>
                        </div>
                      </div>
                      <p className="text-gray-600 font-light leading-relaxed pl-14 border-l-2 border-transparent group-hover/item:border-[var(--color-primary)]/10 transition-colors font-inter">
                        {amenity.description}
                      </p>
                    </div>
                  );
                })}
              </div>
              
              {/* Right Column: Interactive Map Points Sidebar */}
              <div className="lg:col-span-5 order-1 lg:order-2">
                <div className="bg-gray-50/80 backdrop-blur-md p-6 rounded-3xl border border-gray-100 shadow-sm sticky top-8">
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="text-xs font-mono text-[var(--color-primary)] uppercase tracking-widest font-bold">
                      {messages?.interactiveMapPoints || 'Interactive Map Points'}
                    </h4>
                    <div className="flex items-center gap-1 text-[10px] text-gray-400 bg-white px-2 py-1 rounded-full border border-gray-100">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
                      Google Maps
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-3">
                    {/* Use verified places from API if available, otherwise generate search links */}
                    {verifiedPlaces.length > 0 ? (
                      verifiedPlaces.slice(0, 4).map((place, idx) => (
                        <a 
                          key={idx}
                          href={place.uri}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between p-4 rounded-2xl bg-white hover:bg-gray-50 border border-gray-100 hover:border-[var(--color-primary)]/30 transition-all duration-300 group/link shadow-sm hover:shadow-md"
                        >
                          <div className="flex items-center gap-4 min-w-0">
                            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-[var(--color-text)] shrink-0 group-hover/link:bg-[var(--color-primary)] group-hover/link:text-white transition-colors">
                              <MapPin size={14} weight="fill" />
                            </div>
                            <div className="flex flex-col min-w-0">
                              <span className="font-serif text-[var(--color-text)] font-medium truncate text-sm">{place.title}</span>
                              <span className="text-[10px] text-green-600 group-hover/link:text-[var(--color-primary)]/70 transition-colors flex items-center gap-1">
                                <span className="w-1 h-1 rounded-full bg-green-500"></span>
                                {messages?.verifiedLocation || 'Verified location'}
                              </span>
                            </div>
                          </div>
                          <ArrowUpRight size={16} weight="bold" className="text-gray-300 group-hover/link:text-[var(--color-primary)] transition-colors shrink-0" />
                        </a>
                      ))
                    ) : (
                      amenities.map((amenity, idx) => (
                      <a 
                        key={idx}
                        href={`https://www.google.com/maps/search/${encodeURIComponent(amenity.name + ' ' + data.name + ' Mauritius')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-4 rounded-2xl bg-white hover:bg-gray-50 border border-gray-100 hover:border-[var(--color-primary)]/30 transition-all duration-300 group/link shadow-sm hover:shadow-md"
                      >
                        <div className="flex items-center gap-4 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-[var(--color-text)] shrink-0 group-hover/link:bg-[var(--color-primary)] group-hover/link:text-white transition-colors">
                            <MapPin size={14} weight="fill" />
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="font-serif text-[var(--color-text)] font-medium truncate text-sm">{amenity.name}</span>
                            <span className="text-[10px] text-gray-400 group-hover/link:text-[var(--color-primary)]/70 transition-colors">
                              {messages?.tapToViewOnMap || 'Tap to view on map'}
                            </span>
                          </div>
                        </div>
                        <NavigationArrow size={14} weight="bold" className="text-gray-300 group-hover/link:text-[var(--color-primary)] transition-colors shrink-0" />
                </a>
              ))
            )}
                  </div>
                  
                  <div className="mt-6 pt-4 border-t border-gray-200/50 text-center">
                    <p className="text-[10px] text-gray-400 leading-tight font-inter">
                      Clicking items opens precise location in Google Maps.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ========================================= */}
      {/* FULLSCREEN MAP MODAL */}
      {/* ========================================= */}
      {isMapFullScreen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 md:p-8 animate-fadeIn"
          role="dialog"
          aria-modal="true"
          aria-label={`Fullscreen map of ${data.name}`}
        >
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/90 backdrop-blur-sm" 
            onClick={handleCloseMap}
          />

          {/* Modal Container */}
          <div 
            className="relative w-full h-full bg-[#e8e8e8] rounded-3xl overflow-hidden shadow-2xl flex flex-col animate-scaleIn"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Map Tiles - Larger grid for fullscreen */}
            <div className="absolute inset-0">
              {[-3, -2, -1, 0, 1, 2, 3].map(dy => (
                [-4, -3, -2, -1, 0, 1, 2, 3, 4].map(dx => {
                  const fsZoom = 15;
                  const tileX = Math.floor((lng + 180) / 360 * Math.pow(2, fsZoom)) + dx;
                  const tileY = Math.floor((1 - Math.log(Math.tan(lat * Math.PI / 180) + 1 / Math.cos(lat * Math.PI / 180)) / Math.PI) / 2 * Math.pow(2, fsZoom)) + dy;
                  return (
                    // eslint-disable-next-line @next/next/no-img-element -- Map tiles from external CDN
                    <img
                      key={`fs-${dx}-${dy}`}
                      src={`https://a.basemaps.cartocdn.com/light_all/${fsZoom}/${tileX}/${tileY}.png`}
                      alt=""
                      className="absolute w-[256px] h-[256px] object-cover"
                      style={{
                        left: `calc(50% + ${dx * 256}px - 128px)`,
                        top: `calc(50% + ${dy * 256}px - 128px)`,
                      }}
                      loading="lazy"
                    />
                  );
                })
              ))}
            </div>

            {/* Custom Gold Marker */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-[#d4af37]/20 animate-marker-pulse" />
              <div className="relative w-5 h-5 rounded-full bg-[#d4af37] border-[3px] border-white shadow-[0_4px_12px_rgba(0,0,0,0.35)]" />
            </div>

            {/* Modal Controls */}
            <div className="absolute top-6 right-6 z-[1000] flex gap-4">
              <button 
                onClick={handleCloseMap}
                className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-[var(--color-text)] shadow-xl hover:bg-[var(--color-primary)] hover:text-white transition-colors"
                aria-label="Close fullscreen map"
              >
                <X size={24} weight="bold" />
              </button>
            </div>

            {/* Modal Info Panel */}
            <div className="absolute top-6 left-6 z-[1000] max-w-sm opacity-0 md:opacity-100 pointer-events-none md:pointer-events-auto transition-opacity">
              <div className="bg-white/95 backdrop-blur-md p-6 rounded-3xl shadow-xl border border-white/20">
                <h2 className="font-serif text-3xl text-[var(--color-text)] mb-2">
                  {data.name}
                </h2>
                <p className="text-sm text-gray-500 font-light leading-relaxed mb-4 font-inter">
                  {data.description.split('.')[0]}.
                </p>
                
                <div className="grid grid-cols-2 gap-4 text-xs font-mono text-gray-400 border-t border-gray-100 pt-4">
                  <div>
                    <span className="block uppercase tracking-widest mb-1 text-[var(--color-primary)]">
                      Lat
                    </span>
                    <span className="text-[var(--color-text)] font-bold">
                      {data.latitude?.toFixed(4) || '-20.0100'}
                    </span>
                  </div>
                  <div>
                    <span className="block uppercase tracking-widest mb-1 text-[var(--color-primary)]">
                      Long
                    </span>
                    <span className="text-[var(--color-text)] font-bold">
                      {data.longitude?.toFixed(4) || '57.5800'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========================================= */}
      {/* ANIMATION STYLES */}
      {/* ========================================= */}
      <style jsx>{`
        @keyframes subtle-zoom {
          from { transform: scale(1.05); }
          to { transform: scale(1); }
        }
        
        @keyframes bounce-gentle {
          0%, 100% { transform: translateX(-50%) translateY(0); }
          50% { transform: translateX(-50%) translateY(8px); }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        
        @keyframes marker-pulse {
          0% { transform: translate(-50%, -50%) scale(0.5); opacity: 1; }
          100% { transform: translate(-50%, -50%) scale(2.5); opacity: 0; }
        }
        
        .animate-subtle-zoom {
          animation: subtle-zoom 2s ease-out forwards;
        }
        
        .animate-bounce-gentle {
          animation: bounce-gentle 2s ease-in-out infinite;
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        
        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out;
        }
        
        .animate-marker-pulse {
          animation: marker-pulse 2s infinite;
        }
        
        .font-inter {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }
      `}</style>
    </div>
  );
}
